import Vue from 'vue';
import VueI18n from 'vue-i18n';

import './assets/scss/app.scss';

import { getLocale, i18n, load, setLocale } from '../locales';
import ApiFetcher from './ApiFetcher';

import App from './App.vue';
import Toast from './components/Toast/Toast';
import { PrintCalendar } from './PrintCalendar';
import router from './router';
import Stores from './stores/Stores';
import UserRepo from './stores/UserRepo';
import Timeout from './Timeout';
import { Polyfillers } from './Utils';

import './registerServiceWorker';

new Polyfillers(window);

// Prevent double tap because client requested that users not be able to double tap to zoom
let doubleTouchStartTimestamp = 0;
document.addEventListener('click', (event) => {
    // make sure labels with checkboxes still work.
    if (event.target.type === 'checkbox') return;

    const now = +new Date();
    if (doubleTouchStartTimestamp + 500 > now) event.preventDefault();
    doubleTouchStartTimestamp = now;
});

if (process.env.NODE_ENV !== 'production') {
    Vue.config.devtools = true;
    Vue.config.performance = true;
    Vue.config.productionTip = false;
}

Vue.use(VueI18n);

window.apiFetcher = new ApiFetcher();

PrintCalendar.autoLoad();

const VueScrollTo = require('vue-scrollto');
Vue.use(VueScrollTo, {
    container: 'body',
    duration: 500,
    easing: [0.55, 0, 0.1, 1],
    offset: 0,
    cancelable: true,
    onDone: false,
    onCancel: false
});

// development / production folder switch
window.$asset = (src) => {
    const path = window.location.port === '' ? '/signup-dist/' : '/';
    return path + src;
};
Vue.prototype.$asset = window.$asset;

Stores.router = router;
Vue.util.defineReactive(Vue.prototype, '$stores', Stores);
router.afterEach((to, from) => {
    Stores.app.referrer = from;
    // Track page views after route changes
    trackPageView(to);
});
router.beforeEach((to, from, next) => {
    const title = document.querySelector('title[data-base-title]').getAttribute('data-base-title');
    const routeTitle =
        to.meta.title !== undefined
            ? to.meta.title === ''
                ? ''
                : ' | ' + to.meta.title
            : ' | ' + to.name;

    document.title = title + routeTitle;
    next();
});

// GA4 Implementation
// Initialize dataLayer
window.dataLayer = window.dataLayer || [];

function gtag() {
    window.dataLayer.push(arguments);
}

// GA4 Configuration
const GA4_ID = 'G-S9WBXGHHNW';

// Initialize GA4
if (process.env.NODE_ENV === 'production') {
    // Load GA4 script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
    document.head.appendChild(script);

    // Configure GA4
    gtag('js', new Date());
    gtag('config', GA4_ID, {
        page_title: document.title,
        page_location: window.location.href
    });
}

// Custom page tracking function
const trackPageView = (to) => {
    if (process.env.NODE_ENV === 'production') {
        gtag('config', GA4_ID, {
            page_title: document.title,
            page_path: router.options.base + to.path,
            page_location: window.location.href
        });
    }
};

// Make gtag available globally for custom event tracking
Vue.prototype.$gtag = gtag;
window.$gtag = gtag;

// Helper function for custom events
Vue.prototype.$trackEvent = (eventName, parameters = {}) => {
    if (process.env.NODE_ENV === 'production') {
        gtag('event', eventName, parameters);

        // Also push to dataLayer
        window.dataLayer.push({
            event: eventName,
            ...parameters
        });
    }
};
window.$trackEvent = Vue.prototype.$trackEvent;

Vue.prototype.$toast = Toast;
window.$toast = Toast;

// Check break point
const $breakpoint = { val: 'x' };
Vue.util.defineReactive(Vue.prototype, '$breakpoint', $breakpoint);

function checkBreakpoint() {
    $breakpoint.val = window
        .getComputedStyle(document.querySelector('body'), ':before')
        .getPropertyValue('content')
        .replace(/"/g, '');
}

window.addEventListener('resize', checkBreakpoint);
checkBreakpoint();
window.$breakpoint = $breakpoint;

const mountVue = () => {
    load();
    setLocale(getLocale());
    window.rootUrl = `/signup/${window.store.app.state.config.uniqid}`;
    new Vue({ router, i18n, render: (h) => h(App) }).$mount('#app');
};

const timeoutFn = () => {
    Timeout.init(
        // callback to notify user that the session has expired
        () => {
            if (window.store.user.isLoggedIn) {
                UserRepo.logout(false);

                window.showInlineModal({
                    title: this.$t('session.title'),
                    content: `
                    <p class="pt-4 pb-2 text-center">
                        <span class="font-bold">{{$t('session.description[0]')}}</span>
                        <br />
                        {{$t('session.description[1]')}}
                    </p>
                `,
                    footerComponent: {
                        render(h) {
                            const uniqid = window.store.app.state.config.uniqid;

                            return h('div', { class: 'p-2' }, [
                                h(
                                    'button',
                                    {
                                        class: 'btn -primary w-full text-center mb-2',
                                        on: {
                                            click: () => {
                                                window.location =
                                                    window.location.origin +
                                                    '/signup/' +
                                                    uniqid +
                                                    '/login';
                                            }
                                        }
                                    },
                                    [this.$t('session.login_btn')]
                                ),
                                h(
                                    'button',
                                    {
                                        class: 'btn -primary w-full text-center',
                                        on: {
                                            click: () => {
                                                window.location =
                                                    window.location.origin + '/signup/' + uniqid;
                                            }
                                        }
                                    },
                                    [this.$t('session.home_btn')]
                                )
                            ]);
                        }
                    }
                });
            }
        },
        // callback to warn user that session is about to expire
        () => {
            if (window.store.user.isLoggedIn)
                window.$toast.warn({
                    timeout: Timeout.warnSeconds * 1000,
                    body: `This session will end in less than one minute.`
                });
        }
    );
};

//START YOUR ENGINES!!!
// If we didn't get a '/payload' via the 'signupAppPayload' variable then we need to load it first!
// this is useful for development purposes as well as a backup
console.log(process.env.NODE_ENV);
if (process.env.NODE_ENV === 'production') {
    window.store.app.parsePayload(window.signupAppPayload);
    mountVue();
    timeoutFn();
} else {
    window.store.app.getFromServer().then(() => {
        window.themify.setFromHex(
            window.store.app.state &&
                window.store.app.state.config &&
                window.store.app.state.config.group &&
                window.store.app.state.config.group.custom_colour
                ? '#' + window.store.app.state.config.group.custom_colour
                : false
        );
        mountVue();
        timeoutFn();
    });
}
